import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";
import { ensureFreshSubscription } from "./push";
import { useBackgroundRefresh } from "./refresh";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // Auth lives in an httpOnly cookie now — always probe /auth/me; the cookie
    // (or a legacy Bearer token) is attached automatically by the api client.
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Heal stale push subscriptions (bound to an old VAPID key) on every
      // app boot. Transparent to the user — they keep receiving pushes.
      ensureFreshSubscription().catch(() => {});
    } catch (err) {
      // No session (or expired) — clear any legacy token silently.
      if (process.env.NODE_ENV !== "production") console.warn("auth/me failed:", err?.message);
      localStorage.removeItem("fintech_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (rut, pin) => {
    // Backend sets the httpOnly auth cookie on the response. No token is
    // stored in localStorage anymore (XSS protection).
    const { data } = await api.post("/auth/login", { rut, pin });
    setUser(data.user);
    ensureFreshSubscription().catch(() => {});
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    ensureFreshSubscription().catch(() => {});
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("logout endpoint failed:", err?.message);
    }
    localStorage.removeItem("fintech_token"); // clear any legacy token
    setUser(null);
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("refreshMe failed:", err?.message);
    }
  };

  // Re-fetch user (balance, status, tags) whenever the app becomes
  // visible / focused / receives a push. Avoids stale data after the
  // PWA was sitting in the background.
  useBackgroundRefresh(() => {
    if (user) refreshMe();
  });

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
